import * as yup from "yup"
import { InferType } from "yup"
import { ValidationError } from "utils/form"
import { format, isValid, parse, parseISO } from "date-fns"
import { GraphTagRule } from "@features/builder/types"

const GRAPH_TAG_RULES: GraphTagRule[] = ["Never", "Always", "More than one"]

export type MetadataFormValues = InferType<ReturnType<typeof getValidationSchema>>

type MetadataDateValue = MetadataFormValues["start_date"] | MetadataFormValues["end_date"]

const dateStringToUTC = (_value: unknown, originalValue: unknown) => {
  if (originalValue === null || originalValue === undefined) {
    return originalValue
  }

  const date = new Date(originalValue as string | Date)
  date.setTime(date.getTime() - date.getTimezoneOffset() * 60 * 1000)
  return date
}

function getComparableDate(dateValue: MetadataDateValue): string | null {
  if (!dateValue || dateValue === "rolling") {
    return null
  }

  if (dateValue instanceof Date) {
    return isValid(dateValue) ? format(dateValue, "yyyy-MM-dd") : null
  }

  if (typeof dateValue === "string") {
    const isoDate = parseISO(dateValue)

    if (isValid(isoDate)) {
      return format(isoDate, "yyyy-MM-dd")
    }

    const slashDate = parse(dateValue, "yyyy/MM/dd", new Date())

    return isValid(slashDate) ? format(slashDate, "yyyy-MM-dd") : null
  }

  return null
}

function getComparableStartDate(dateValue: MetadataFormValues["start_date"]): string | null {
  if (dateValue === "rolling") {
    return format(new Date(), "yyyy-MM-dd")
  }

  return getComparableDate(dateValue)
}

export function getValidationSchema() {
  return yup.object().shape({
    name: yup
      .string()
      .required(ValidationError.REQUIRED)
      .matches(/^[a-zA-Z0-9\s-]+$/, "Name can only contain letters, numbers, spaces, and hyphens"),
    description: yup.string().nullable(),
    product_key: yup.string().required(ValidationError.REQUIRED),
    start_date: yup.lazy((value) => {
      if (value === "rolling") return yup.string()
      return yup.date().transform(dateStringToUTC).required(ValidationError.REQUIRED)
    }),
    end_date: yup.lazy((value) => {
      if (value === "rolling") return yup.string().required(ValidationError.REQUIRED)
      return yup
        .date()
        .transform(dateStringToUTC)
        .required(ValidationError.REQUIRED)
        .test({
          name: "end-date-after-start-date",
          test(endDateValue) {
            const startDate = getComparableStartDate(this.parent?.start_date)
            const endDate = getComparableDate(endDateValue)

            if (!startDate || !endDate || endDate > startDate) {
              return true
            }

            return this.createError({
              message: ValidationError.DATE_AFTER(startDate),
            })
          },
        })
    }),
    simulation_frequency: yup.string().oneOf(["yearly", "quarterly", "monthly", ""]).nullable(),
    yearly_rolling_start_month: yup.number().nullable(),
    rolling_duration: yup.number().nullable(),
    scenario_source: yup.mixed<string | File>().nullable(),
    scenario_version: yup.string().optional(),
    scenarios: yup.array().of(yup.string().required()).optional(),
    scenario_descriptions: yup
      .array()
      .of(
        yup.object().shape({
          Name: yup.string().required(),
          Description: yup.string().max(1000, ValidationError.MAX_LENGTH(1000)).optional(),
        }),
      )
      .optional(),
    owner_group: yup.string().nullable(),
    tags: yup
      .array()
      .of(
        yup.object().shape({
          Name: yup.string().required(),
          Rule: yup.mixed<GraphTagRule>().oneOf(GRAPH_TAG_RULES).required(),
          Description: yup.string().optional(),
          Values: yup.array().of(yup.string().required()).required(),
        }),
      )
      .optional(),
    overview: yup.string().max(2000, ValidationError.MAX_LENGTH(2000)),
  })
}
